import { formatPairingCode } from '@forgesworn/joystick-protocol';
/**
 * Tilt Race canvas renderer. Letterboxes the ARENA_W x ARENA_H world
 * to fill the viewport, draws walls + pits + finish line + each
 * player's marble + a corner pairing tile / score badge per slot
 * (same metaphor as Tank Royale's spawn corners but stretched along
 * the left edge since players race left-to-right).
 */

import {
  ARENA_W, ARENA_H, COUNTDOWN_S, FINISH_X, MARBLE_RADIUS, MAX_PLAYERS,
  PLAYER_COLOURS, PITS, WALLS,
  humanCount,
  type Marble, type World,
} from './game.js';

export interface CheerBurst {
  slot: number;
  /** performance.now() ms timestamp at which the burst expires. */
  until: number;
  colour: string;
}

export interface RenderFrame {
  world: World;
  pairingUrl: string;
  qrDataUrl: string | null;
  /** The pairing code (the session id). Printed under the QR as the
   *  type-it-in fallback for players who cannot scan. */
  sessionId: string;
  joinState: Map<number, 'open' | 'taken'>;
  muted: boolean;
  /** Slot → running cross-game tournament total announced by the phone. */
  tournamentTotals: Map<number, number>;
  /** Slot → optional display name announced by the phone. */
  playerNames: Map<number, string>;
  /** Active cheer bursts from spectator phones. */
  cheers: CheerBurst[];
}

interface Geom { scale: number; offsetX: number; offsetY: number }

export function fitCanvas(canvas: HTMLCanvasElement): Geom {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const ww = window.innerWidth, wh = window.innerHeight;
  canvas.width = Math.floor(ww * dpr);
  canvas.height = Math.floor(wh * dpr);
  canvas.style.width = `${ww}px`;
  canvas.style.height = `${wh}px`;
  const scale = Math.min(ww / ARENA_W, wh / ARENA_H);
  const offsetX = (ww - ARENA_W * scale) / 2;
  const offsetY = (wh - ARENA_H * scale) / 2;
  return { scale: scale * dpr, offsetX: offsetX * dpr, offsetY: offsetY * dpr };
}

export function draw(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  const geom = fitCanvas(ctx.canvas);
  ctx.fillStyle = '#040410';
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  let shakeX = 0, shakeY = 0;
  if (frame.world.shake) {
    const elapsed = frame.world.time - frame.world.shake.startedAt;
    const t = Math.max(0, 1 - elapsed / frame.world.shake.duration);
    const amp = frame.world.shake.intensity * t * t;
    shakeX = (Math.random() * 2 - 1) * amp;
    shakeY = (Math.random() * 2 - 1) * amp;
  }
  ctx.save();
  ctx.translate(geom.offsetX + shakeX * geom.scale, geom.offsetY + shakeY * geom.scale);
  ctx.scale(geom.scale, geom.scale);
  drawArena(ctx, frame.world.time);
  drawPits(ctx, frame.world.time);
  drawWalls(ctx);
  drawFinish(ctx);
  for (const m of frame.world.marbles.values()) drawMarble(ctx, m, frame.world.time);
  drawParticles(ctx, frame);
  drawConfettiCurtain(ctx, frame);
  ctx.restore();
  drawLanes(ctx, frame, geom);
  drawLobbyOverlay(ctx, frame);
  drawAttractOverlay(ctx, frame);
  drawCountdownOverlay(ctx, frame);
  drawWinnerOverlay(ctx, frame);
  drawCheers(ctx, frame);
  drawMuteHint(ctx, frame);
}

function drawAttractOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (!frame.world.attractMode) return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const time = frame.world.time;
  const breath = 0.7 + 0.3 * Math.abs(Math.sin(time * 1.6));
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${Math.floor(18 * dpr)}px ui-monospace, monospace`;
  ctx.fillStyle = `rgba(255, 216, 74, ${0.5 + 0.5 * breath})`;
  ctx.shadowColor = '#ffd84a';
  ctx.shadowBlur = 10 * dpr * breath;
  ctx.fillText('ATTRACT MODE · SCAN A LANE TO TAKE OVER', ctx.canvas.width / 2, 32 * dpr);
  ctx.shadowBlur = 0;
  ctx.restore();
}

function drawCheers(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.cheers.length === 0) return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const now = performance.now();
  const w = ctx.canvas.width;
  ctx.save();
  for (const cheer of frame.cheers) {
    const remainingMs = Math.max(0, cheer.until - now);
    const t = remainingMs / 1200;
    const alpha = Math.min(1, t * 1.4);
    const baseY = ctx.canvas.height - 70 * dpr;
    ctx.globalAlpha = alpha;
    const grad = ctx.createLinearGradient(0, baseY, w, baseY);
    grad.addColorStop(0, 'transparent');
    grad.addColorStop(0.5, cheer.colour);
    grad.addColorStop(1, 'transparent');
    ctx.fillStyle = grad;
    ctx.fillRect(0, baseY, w, 4 * dpr);
    ctx.fillStyle = cheer.colour;
    ctx.font = `bold ${Math.floor(24 * dpr)}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = cheer.colour;
    ctx.shadowBlur = 18 * dpr;
    ctx.fillText(`P${cheer.slot + 1} CHEERS`, w / 2, baseY - 28 * dpr);
    ctx.shadowBlur = 0;
  }
  ctx.restore();
}

function drawCountdownOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.race.kind !== 'countdown') return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const elapsed = frame.world.time - frame.world.race.startedAt;
  const remaining = COUNTDOWN_S - elapsed;
  let label: string;
  let beatT: number;
  if (remaining > 2.2) { label = '3'; beatT = (COUNTDOWN_S - remaining); }
  else if (remaining > 1.2) { label = '2'; beatT = 1.0 - (remaining - 1.2); }
  else if (remaining > 0.2) { label = '1'; beatT = 1.0 - (remaining - 0.2); }
  else { label = 'GO!'; beatT = 1.0 - (remaining / 0.2); }
  const pulse = 1 - Math.pow(Math.max(0, beatT - 0.05) / 0.95, 2);
  const size = Math.floor((90 + 30 * pulse) * dpr);
  const cx = ctx.canvas.width / 2;
  const cy = ctx.canvas.height / 2;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${size}px ui-monospace, monospace`;
  const colour = label === 'GO!' ? '#58ff58' : '#ffd84a';
  ctx.shadowColor = colour;
  ctx.shadowBlur = 32 * dpr;
  ctx.fillStyle = colour;
  ctx.fillText(label, cx, cy);
  ctx.restore();
}

function drawMuteHint(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  const dpr = ctx.canvas.width / window.innerWidth;
  ctx.save();
  ctx.font = `${Math.floor(11 * dpr)}px ui-monospace, monospace`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  ctx.fillStyle = frame.muted ? '#ffd84a' : 'rgba(207,216,255,0.55)';
  ctx.fillText(frame.muted ? 'MUTED · M' : 'AUDIO · M',
    ctx.canvas.width - 16 * dpr, 16 * dpr);
  ctx.restore();
}

function drawLobbyOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.race.kind !== 'lobby') return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const humans = humanCount(frame.world);
  const time = frame.world.time;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = ctx.canvas.width / 2;
  const cy = ctx.canvas.height / 2 - 20 * dpr;
  if (humans === 0) {
    const breath = 1 + Math.sin(time * 2.6) * 0.04;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(breath, breath);
    ctx.font = `bold ${Math.floor(54 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#cfd8ff';
    ctx.shadowColor = '#58ff58';
    ctx.shadowBlur = (18 + 8 * Math.sin(time * 3)) * dpr;
    ctx.fillText('SCAN A LANE TO JOIN', 0, 0);
    ctx.shadowBlur = 0;
    ctx.restore();
    ctx.font = `${Math.floor(20 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#8a92b8';
    ctx.fillText('Open pad.forgesworn.dev on your phone · scan any lane', cx, cy + 56 * dpr);
    // Pointing chevron toward the left edge where lanes sit.
    const chevX = ctx.canvas.width * 0.22;
    const chevY = ctx.canvas.height / 2 + Math.sin(time * 4) * 6 * dpr;
    ctx.save();
    ctx.translate(chevX, chevY);
    ctx.rotate(Math.PI);
    ctx.fillStyle = '#58ff58';
    ctx.shadowColor = '#58ff58';
    ctx.shadowBlur = 14 * dpr;
    ctx.beginPath();
    ctx.moveTo(0, -18 * dpr); ctx.lineTo(14 * dpr, 12 * dpr); ctx.lineTo(-14 * dpr, 12 * dpr); ctx.closePath();
    ctx.fill();
    ctx.restore();
  } else {
    ctx.font = `bold ${Math.floor(50 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#58ff58';
    ctx.shadowColor = '#58ff58';
    ctx.shadowBlur = 22 * dpr;
    ctx.fillText('TILT OR TAP A TO START', cx, cy);
    ctx.shadowBlur = 0;
    ctx.font = `${Math.floor(22 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#8a92b8';
    ctx.fillText(`${humans} ready · bots will take the empty lanes`, cx, cy + 52 * dpr);
  }
  ctx.restore();
}

function drawParticles(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const p of frame.world.particles) {
    const t = Math.max(0, p.life / p.maxLife);
    ctx.fillStyle = p.colour;
    ctx.globalAlpha = t;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (0.5 + t * 0.5), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawArena(ctx: CanvasRenderingContext2D, time: number): void {
  // Gradient floor — slight gradient east→west so the finish has
  // visual weight.
  const g = ctx.createLinearGradient(0, 0, ARENA_W, 0);
  g.addColorStop(0, '#0a0a18');
  g.addColorStop(0.6, '#0e0e22');
  g.addColorStop(1, '#15152e');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, ARENA_W, ARENA_H);
  ctx.strokeStyle = 'rgba(120, 130, 200, 0.06)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  const step = 50;
  for (let x = step; x < ARENA_W; x += step) { ctx.moveTo(x, 0); ctx.lineTo(x, ARENA_H); }
  for (let y = step; y < ARENA_H; y += step) { ctx.moveTo(0, y); ctx.lineTo(ARENA_W, y); }
  ctx.stroke();
  // Parallax — horizontal lines drifting leftward, hinting at forward
  // momentum. Three layers at different speeds.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const layer of [{ speed: 60, alpha: 0.04, count: 8 }, { speed: 120, alpha: 0.05, count: 12 }, { speed: 200, alpha: 0.04, count: 16 }]) {
    ctx.strokeStyle = `rgba(180, 195, 245, ${layer.alpha})`;
    ctx.lineWidth = 1;
    for (let i = 0; i < layer.count; i++) {
      const baseY = ((i + 0.5) * (ARENA_H / layer.count));
      const offsetX = (time * layer.speed + i * 71) % (ARENA_W + 200);
      const x = (offsetX - 200);
      ctx.beginPath();
      ctx.moveTo(x, baseY);
      ctx.lineTo(x + 60, baseY);
      ctx.stroke();
    }
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(207, 216, 255, 0.30)';
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, ARENA_W - 4, ARENA_H - 4);
}

function drawPits(ctx: CanvasRenderingContext2D, time: number): void {
  for (const pit of PITS) {
    const g = ctx.createRadialGradient(pit.x, pit.y, 4, pit.x, pit.y, pit.r);
    g.addColorStop(0, 'rgba(180, 60, 200, 0.85)');
    g.addColorStop(0.7, 'rgba(60, 20, 80, 0.65)');
    g.addColorStop(1, 'rgba(10, 10, 20, 0.0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(pit.x, pit.y, pit.r, 0, Math.PI * 2);
    ctx.fill();
    // Vortex — three short arcs orbiting the pit centre, each at a
    // different radius + phase, conveying that this is a hazard you
    // get sucked into rather than a flat circle.
    ctx.save();
    ctx.translate(pit.x, pit.y);
    ctx.rotate(time * 1.8);
    ctx.strokeStyle = 'rgba(220, 180, 255, 0.55)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      const r = pit.r * (0.35 + i * 0.18);
      const start = (i * Math.PI * 0.6);
      ctx.beginPath();
      ctx.arc(0, 0, r, start, start + Math.PI * 0.5);
      ctx.stroke();
    }
    ctx.restore();
    ctx.strokeStyle = 'rgba(207, 216, 255, 0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(pit.x, pit.y, pit.r, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawWalls(ctx: CanvasRenderingContext2D): void {
  for (const w of WALLS) {
    ctx.fillStyle = 'rgba(120, 130, 200, 0.18)';
    ctx.fillRect(w.x, w.y, w.w, w.h);
    ctx.strokeStyle = 'rgba(207, 216, 255, 0.45)';
    ctx.lineWidth = 2;
    ctx.strokeRect(w.x + 1, w.y + 1, w.w - 2, w.h - 2);
  }
}

function drawFinish(ctx: CanvasRenderingContext2D): void {
  const w = ARENA_W - FINISH_X;
  ctx.save();
  // Checker stripe + glow.
  const cells = 18;
  const cellW = w / 2;
  const cellH = ARENA_H / cells;
  for (let i = 0; i < cells; i++) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.92)' : 'rgba(20,20,30,0.92)';
    ctx.fillRect(FINISH_X,        i * cellH, cellW, cellH);
    ctx.fillStyle = i % 2 === 0 ? 'rgba(20,20,30,0.92)' : 'rgba(255,255,255,0.92)';
    ctx.fillRect(FINISH_X + cellW, i * cellH, cellW, cellH);
  }
  ctx.shadowColor = '#ffffff';
  ctx.shadowBlur = 18;
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(FINISH_X, 0); ctx.lineTo(FINISH_X, ARENA_H);
  ctx.stroke();
  ctx.restore();
}

/** Confetti curtain — overlays the finish-line band whenever at
 *  least one marble has finished. Stateless: deterministic per-flake
 *  seeds + time-driven fall, with random-looking phase. */
function drawConfettiCurtain(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  // Only confetti while there's something to celebrate.
  const anyFinished = [...frame.world.marbles.values()].some((m) => m.finishedAt !== null);
  if (!anyFinished) return;
  const t = frame.world.time;
  const COUNT = 80;
  const bandLeft = FINISH_X - 40;
  const bandWidth = (ARENA_W - bandLeft);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < COUNT; i++) {
    const seedX = (i * 73.13) % bandWidth;
    const colour = CONFETTI_COLOURS[i % CONFETTI_COLOURS.length]!;
    const cycleS = 1.4 + ((i * 0.07) % 0.8);
    const phaseS = (i * 0.31) % cycleS;
    const yProg = ((t + phaseS) % cycleS) / cycleS;
    const x = bandLeft + seedX + Math.sin((t + i) * 2.2) * 12;
    const y = yProg * ARENA_H;
    const a = 0.65 * (1 - Math.pow(yProg - 0.5, 2) * 1.4);
    ctx.fillStyle = colour;
    ctx.globalAlpha = Math.max(0, a);
    ctx.fillRect(x, y, 4, 8);
  }
  ctx.restore();
}
const CONFETTI_COLOURS = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a', '#ff5fa5', '#5fdbff'];

function drawMarble(ctx: CanvasRenderingContext2D, m: Marble, time: number): void {
  ctx.save();
  // Motion blur — five fading copies at recent positions along the
  // velocity vector. Stronger taper when boosting.
  const speed = Math.hypot(m.vx, m.vy);
  const boosting = time < m.boostUntil;
  if (speed > 30) {
    const STEPS = boosting ? 7 : 5;
    const trailLen = boosting ? Math.min(120, speed / 3) : Math.min(70, speed / 4);
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 1; i <= STEPS; i++) {
      const t = i / STEPS;
      const cx = m.x - (m.vx / speed) * trailLen * t;
      const cy = m.y - (m.vy / speed) * trailLen * t;
      const r = MARBLE_RADIUS * (1 - t * 0.6);
      ctx.fillStyle = m.colour;
      ctx.globalAlpha = (boosting ? 0.55 : 0.32) * (1 - t);
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Bright white core trail when boosting — punches above the
    // coloured trail for a "jet exhaust" feel.
    if (boosting) {
      for (let i = 1; i <= 4; i++) {
        const t = i / 4;
        const cx = m.x - (m.vx / speed) * trailLen * t * 0.6;
        const cy = m.y - (m.vy / speed) * trailLen * t * 0.6;
        ctx.fillStyle = 'rgba(255, 255, 255, ' + (0.55 * (1 - t)) + ')';
        ctx.beginPath();
        ctx.arc(cx, cy, MARBLE_RADIUS * 0.5 * (1 - t * 0.4), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
  // Marble body
  ctx.fillStyle = m.colour;
  ctx.shadowColor = m.colour;
  ctx.shadowBlur = 16;
  ctx.beginPath();
  ctx.arc(m.x, m.y, MARBLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  // Inner highlight
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.beginPath();
  ctx.arc(m.x - MARBLE_RADIUS * 0.3, m.y - MARBLE_RADIUS * 0.3, MARBLE_RADIUS * 0.35, 0, Math.PI * 2);
  ctx.fill();
  // Boost pulse halo
  if (time < m.boostUntil) {
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 24;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(m.x, m.y, MARBLE_RADIUS + 9, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Per-player status strip along the bottom of the arena + one big
 *  shared hero QR card in the top-right. The hero QR is the scan
 *  target — any phone scanning it gets the next free lane. The strip
 *  is informational only (no individual QRs), which means it can stay
 *  compact and never crowd into the action. */
function drawLanes(ctx: CanvasRenderingContext2D, frame: RenderFrame, geom: Geom): void {
  drawHeroQr(ctx, frame, geom);
  drawLaneStrip(ctx, frame, geom);
}

function drawHeroQr(ctx: CanvasRenderingContext2D, frame: RenderFrame, geom: Geom): void {
  // Show the hero card whenever there's at least one open lane (any
  // free slot up to MAX_PLAYERS). Hide during pure-paired play so the
  // arena is uncluttered.
  let openCount = 0;
  for (let s = 0; s < MAX_PLAYERS; s++) {
    if ((frame.joinState.get(s) ?? 'open') === 'open') openCount += 1;
  }
  if (openCount === 0) return;

  const dpr = ctx.canvas.width / window.innerWidth;
  const aTop = geom.offsetY;
  const aRight = geom.offsetX + ARENA_W * geom.scale;
  // Big QR — anchored top-right, sized to ~26% of the canvas's smaller
  // dimension so it always reads from across the room.
  const smaller = Math.min(ctx.canvas.width, ctx.canvas.height);
  const card = Math.min(360 * dpr, smaller * 0.30);
  const inset = 22 * dpr;
  const x = aRight - inset - card;
  const y = aTop + inset;
  const time = frame.world.time;
  // Pulsing border + ping ring so the eye snaps to it.
  const phase = (time % 2.2) / 2.2;
  const ringR = card * 0.55 + phase * card * 0.45;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = '#58ff58';
  ctx.globalAlpha = (1 - phase) * 0.5;
  ctx.lineWidth = 3 * dpr;
  ctx.beginPath();
  ctx.arc(x + card / 2, y + card / 2, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = 'rgba(10, 10, 18, 0.92)';
  ctx.fillRect(x, y, card, card + 68 * dpr);
  const breathe = 0.55 + 0.45 * Math.abs(Math.sin(time * 2.4));
  ctx.strokeStyle = '#58ff58';
  ctx.lineWidth = (2 + breathe * 1.8) * dpr;
  ctx.shadowColor = '#58ff58';
  ctx.shadowBlur = 14 * dpr * breathe;
  ctx.strokeRect(x + dpr, y + dpr, card - 2 * dpr, card + 68 * dpr - 2 * dpr);
  ctx.shadowBlur = 0;
  // Header
  ctx.fillStyle = '#58ff58';
  ctx.font = `bold ${Math.floor(18 * dpr)}px ui-monospace, monospace`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'center';
  ctx.fillText('SCAN TO JOIN', x + card / 2, y + 14 * dpr);
  // QR image
  if (frame.qrDataUrl) {
    if (cachedQrImg === null || cachedQrSrc !== frame.qrDataUrl) {
      const img = new Image();
      img.src = frame.qrDataUrl;
      cachedQrImg = img;
      cachedQrSrc = frame.qrDataUrl;
    }
    const qrSize = card - 60 * dpr;
    const qx = x + (card - qrSize) / 2;
    const qy = y + 44 * dpr;
    if (cachedQrImg.complete) {
      ctx.drawImage(cachedQrImg, qx, qy, qrSize, qrSize);
    } else {
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(qx, qy, qrSize, qrSize);
    }
  }
  // Sub-lines: the can't-scan fallback. A player opens the pad URL and
  // types the code, so the code is the loud line, the URL the quiet one.
  ctx.fillStyle = '#8a92b8';
  ctx.font = `${Math.floor(12 * dpr)}px ui-monospace, monospace`;
  ctx.fillText('OR ENTER AT PAD.FORGESWORN.DEV', x + card / 2, y + card + 13 * dpr);
  ctx.fillStyle = '#58ff58';
  ctx.font = `bold ${Math.floor(26 * dpr)}px ui-monospace, monospace`;
  ctx.shadowColor = '#58ff58';
  ctx.shadowBlur = 8 * dpr;
  ctx.fillText(formatPairingCode(frame.sessionId), x + card / 2, y + card + 33 * dpr);
  ctx.shadowBlur = 0;
}

function drawLaneStrip(ctx: CanvasRenderingContext2D, frame: RenderFrame, geom: Geom): void {
  const dpr = ctx.canvas.width / window.innerWidth;
  const aLeft = geom.offsetX;
  const aRight = geom.offsetX + ARENA_W * geom.scale;
  const aBottom = geom.offsetY + ARENA_H * geom.scale;
  // Horizontal strip along the bottom — 4 equal pills.
  const inset = 16 * dpr;
  const stripH = 72 * dpr;
  const stripY = aBottom - inset - stripH;
  const stripWidth = aRight - aLeft - inset * 2;
  const stripX = aLeft + inset;
  const gap = 10 * dpr;
  const pillW = (stripWidth - gap * (MAX_PLAYERS - 1)) / MAX_PLAYERS;
  for (let slot = 0; slot < MAX_PLAYERS; slot++) {
    const px = stripX + slot * (pillW + gap);
    const py = stripY;
    drawLanePill(ctx, px, py, pillW, stripH, slot, frame, dpr);
  }
}

function drawLanePill(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  slot: number, frame: RenderFrame, dpr: number,
): void {
  const colour = PLAYER_COLOURS[slot]!;
  const state = frame.joinState.get(slot) ?? 'open';
  const marble = frame.world.marbles.get(slot);
  // Background
  ctx.fillStyle = 'rgba(10, 10, 18, 0.78)';
  ctx.fillRect(x, y, w, h);
  // Left accent stripe — solid for taken, dashed for open
  ctx.fillStyle = colour;
  ctx.fillRect(x, y, 4 * dpr, h);
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.5 * dpr;
  if (state === 'open') {
    ctx.setLineDash([4 * dpr, 4 * dpr]);
  } else {
    ctx.setLineDash([]);
  }
  ctx.strokeRect(x + dpr, y + dpr, w - 2 * dpr, h - 2 * dpr);
  ctx.setLineDash([]);
  // Lane label
  ctx.fillStyle = colour;
  ctx.font = `bold ${Math.floor(14 * dpr)}px ui-monospace, monospace`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(`P${slot + 1}`, x + 12 * dpr, y + 8 * dpr);
  // Status
  if (state === 'open') {
    ctx.fillStyle = '#8a92b8';
    ctx.font = `${Math.floor(12 * dpr)}px ui-monospace, monospace`;
    ctx.fillText('OPEN — SCAN THE HERO QR', x + 12 * dpr, y + 28 * dpr);
    return;
  }
  if (!marble) return;
  // Taken — show name + score + tournament total
  const displayName = !marble.ai ? frame.playerNames.get(slot) : null;
  const nameStr = marble.ai
    ? 'BOT'
    : (displayName ? displayName : 'PLAYER');
  ctx.fillStyle = '#cfd8ff';
  ctx.font = `${Math.floor(13 * dpr)}px ui-monospace, monospace`;
  ctx.fillText(nameStr, x + 12 * dpr, y + 28 * dpr);
  // Right side: score + (optional) tournament total
  ctx.font = `bold ${Math.floor(22 * dpr)}px ui-monospace, monospace`;
  ctx.textAlign = 'right';
  ctx.fillStyle = colour;
  ctx.fillText(String(marble.score), x + w - 12 * dpr, y + 10 * dpr);
  const tt = frame.tournamentTotals.get(slot);
  if (typeof tt === 'number' && !marble.ai) {
    ctx.font = `${Math.floor(10 * dpr)}px ui-monospace, monospace`;
    ctx.fillStyle = '#b48cff';
    ctx.fillText(`Σ ${tt}`, x + w - 12 * dpr, y + 40 * dpr);
  }
}

let cachedQrImg: HTMLImageElement | null = null;
let cachedQrSrc: string | null = null;

function drawWinnerOverlay(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  if (frame.world.race.kind !== 'over') return;
  const dpr = ctx.canvas.width / window.innerWidth;
  const winnerP = frame.world.race.winnerP;
  const winner = winnerP !== null ? frame.world.marbles.get(winnerP) : null;
  const elapsed = frame.world.time - frame.world.race.endedAt;

  ctx.save();
  const overlayAlpha = Math.min(0.55, elapsed * 1.4);
  ctx.fillStyle = `rgba(4, 4, 16, ${overlayAlpha})`;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Confetti cascade — same shape as tank's, slightly slower so the
  // race close feels longer.
  if (elapsed < 5) {
    const STREAMS = 50;
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < STREAMS; i++) {
      const colour = WIN_CONFETTI_TILT[i % WIN_CONFETTI_TILT.length]!;
      const seedX = (i * 137.13) % 1;
      const swayPhase = (i * 0.29);
      const cycleS = 1.5 + ((i * 0.05) % 0.8);
      const startDelay = (i * 0.024) % 1.2;
      const tNow = elapsed - startDelay;
      if (tNow < 0) continue;
      const yProg = (tNow / cycleS) % 1;
      const x = seedX * ctx.canvas.width + Math.sin(tNow * 2.4 + swayPhase) * 16 * dpr;
      const y = yProg * ctx.canvas.height * 1.1;
      const a = Math.max(0, 0.85 - Math.pow(yProg - 0.55, 2) * 1.6) * Math.min(1, tNow * 1.4);
      if (a <= 0) continue;
      ctx.fillStyle = colour;
      ctx.globalAlpha = a;
      ctx.fillRect(x, y, 4 * dpr, 9 * dpr);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const headT = Math.min(1, elapsed / 0.45);
  const headScale = 0.7 + (1 - Math.pow(1 - headT, 3)) * 0.35 + Math.sin(headT * Math.PI) * 0.06;
  ctx.save();
  ctx.translate(ctx.canvas.width / 2, ctx.canvas.height / 2 - 32 * dpr);
  ctx.scale(headScale, headScale);
  ctx.font = `bold ${Math.floor(96 * dpr)}px ui-monospace, monospace`;
  const winnerName = winner && !winner.ai ? (frame.playerNames.get(winner.p) ?? null) : null;
  if (winner) {
    ctx.fillStyle = winner.colour;
    ctx.shadowColor = winner.colour;
    ctx.shadowBlur = 28 * dpr;
    ctx.fillText(winnerName ? `${winnerName} WINS` : `P${winner.p + 1} WINS`, 0, 0);
  } else {
    ctx.fillStyle = '#cfd8ff';
    ctx.shadowBlur = 0;
    ctx.fillText('NO FINISHERS', 0, 0);
  }
  ctx.restore();
  ctx.shadowBlur = 0;
  const subAlpha = Math.min(1, Math.max(0, (elapsed - 0.5) * 2));
  ctx.font = `${Math.floor(24 * dpr)}px ui-monospace, monospace`;
  ctx.fillStyle = `rgba(207, 216, 255, ${0.8 * subAlpha})`;
  ctx.fillText('NEXT RACE...', ctx.canvas.width / 2, ctx.canvas.height / 2 + 60 * dpr);
  ctx.restore();
}
const WIN_CONFETTI_TILT = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a', '#ff5fa5', '#5fdbff', '#ffffff'];
